'use client';

/**
 * PortalLayout Component
 *
 * Generic layout shell for all portals. Supports two sidebar modes:
 * - 'classic' (default): Traditional 240px drawer (UnifiedSidebar)
 * - 'rail': Two-layer sidebar (60px icon rail + 200px expandable panel)
 *
 * Section tabs are provided via SectionTabsContext so PageTemplate can
 * render them inside PageContentHeader in-content.
 *
 * @see Issue #330 - Unified Navigation System
 * @see PR #856 — Navigation & Layout Redesign
 *
 * LAYOUT PATTERN: Nested Overflow Containment
 * ===========================================
 * - Outer Box (overflow: hidden) - prevents body scroll, contains viewport
 * - Main content Box (overflow: hidden) - prevents intermediate scroll
 * - Container (overflow: hidden) - constrains width
 * - Inner content Box (overflow: auto) - scrollable content area
 */

import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import type { NavItem, NavSection, SidebarConfig, VisibilityContext } from '@ops-matrix/shared-types';
import { checkVisibility, filterNavSections } from '@ops-matrix/shared-types';
import type React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  NavigationProgressBar,
  NavigationProgressProvider,
  useNavigationProgress,
} from '../../contexts/NavigationProgressContext';
import { SectionTabsProvider } from '../../contexts/SectionTabsContext';
import { SidebarWidthProvider } from '../../contexts/SidebarWidthContext';
import { useI18n } from '../../hooks/useI18n';
import { LAYOUT_HEIGHTS, LayoutOffsetProvider } from '../../hooks/useLayoutOffset';
import { PANEL_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH, useSidebarRail } from '../../hooks/useSidebarRail';
import { sortItemsByTranslatedLabel } from '../../utils/sortNavItems';
import { HEADER_HEIGHT } from '../AppNavBar/AppNavBar';
import { SidebarPanel } from '../SidebarPanel/SidebarPanel';
import { SidebarRail } from '../SidebarRail/SidebarRail';
import { UnifiedSidebar } from '../UnifiedSidebar/UnifiedSidebar';

// ============================================================================
// Constants
// ============================================================================

const DRAWER_WIDTH = 240;

// ============================================================================
// Types
// ============================================================================

export interface PortalLayoutProps {
  /** Page content */
  children: React.ReactNode;

  /** Current URL pathname (from router) */
  pathname: string;

  /** Navigation callback (framework-agnostic) */
  onNavigate: (path: string) => void;

  /**
   * Optional hover prefetch hook forwarded to the sidebar rail.
   * Portals wire this to `router.prefetch` (Next.js) to warm the next
   * route's JS + data before the click lands.
   */
  onHoverPath?: (path: string) => void;

  /** Sidebar navigation configuration */
  navConfig: SidebarConfig;

  /** Visibility context for filtering nav items and tabs */
  visibilityContext: VisibilityContext;

  /**
   * Dynamic badge counts per nav-item id. Threaded through to SidebarPanel /
   * UnifiedSidebar, both of which already support this prop. Falls back to
   * `NavItem.badge.count` (static) when not provided.
   */
  badgeCounts?: Record<string, number>;

  /** Whether an impersonation banner is currently shown */
  impersonating?: boolean;

  /** Height of the impersonation banner in pixels (default: 0) */
  bannerHeight?: number;

  /** Render the header bar. Receives mobile menu state + branding props for the full-width header. */
  renderHeader: (props: {
    showMenuButton: boolean;
    onMenuClick: () => void;
    portalName?: string;
    onLogoClick?: () => void;
    /** True when sidebar rail is active — avatar is in the rail, hide from header */
    hideUserAvatar?: boolean;
  }) => React.ReactNode;

  /** Optional breadcrumbs slot */
  renderBreadcrumbs?: () => React.ReactNode;

  /** Optional banner slot (e.g., impersonation banner) rendered at the very top */
  renderBanner?: () => React.ReactNode;

  /** Handler for logo/header click in sidebar (typically navigate to dashboard) */
  onLogoClick?: () => void;

  // ============================================================================
  // User (avatar lives in TopBar by default — see v3 design shell.jsx:191-194)
  // ============================================================================
  /** Current user for the avatar */
  user?: { name?: string; email?: string; avatarUrl?: string };
  /**
   * Where the user avatar lives. Defaults to `'header'` to match the v3
   * design (top-right of the topbar). `'rail'` keeps the legacy
   * bottom-of-rail Asana-style placement for portals that haven't migrated.
   */
  userAvatarPlacement?: 'header' | 'rail';
  /** Handler for logout */
  onLogout?: () => void;
  /** Handler for profile navigation */
  onProfile?: () => void;
  /** Handler for Control Center navigation */
  onControlCenter?: () => void;
  /** i18n key for the settings menu item label in the avatar menu (default: 'navigation.settings') */
  settingsMenuLabel?: string;
  /** Whether dark mode is active (for theme toggle in rail avatar menu) */
  isDarkMode?: boolean;
  /** Handler for theme toggle (from rail avatar menu) */
  onThemeToggle?: () => void;
  /**
   * Hover-expand mode for the rail sidebar (rail layoutMode only). When
   * true, the panel auto-shows on hover and auto-hides on cursor leave;
   * the toggle button latches the panel as a pin/unpin action. First
   * visit defaults to `pinned=false` so the user sees just the icon
   * rail. Existing portals stay on the always-pinned default.
   */
  hoverExpand?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get the active nav item based on current pathname.
 * Checks standalone items, section items, and tab paths.
 * Uses longest-path-wins strategy to avoid parent paths matching child routes
 * (e.g., /control-tower matching before /control-tower/scenarios).
 */
export function getActiveNavItem(pathname: string, config: SidebarConfig): NavItem | null {
  let bestMatch: NavItem | null = null;
  let bestMatchLength = -1;

  // Helper: update best match if this path is longer (more specific)
  const considerMatch = (item: NavItem, matchPath: string) => {
    if ((pathname === matchPath || pathname.startsWith(`${matchPath}/`)) && matchPath.length > bestMatchLength) {
      bestMatch = item;
      bestMatchLength = matchPath.length;
    }
  };

  // Check standalone items
  for (const item of config.standaloneItems || []) {
    considerMatch(item, item.path);
  }

  // Check section items and their tabs
  for (const section of config.sections) {
    for (const item of section.items) {
      considerMatch(item, item.path);
      if (item.tabs) {
        for (const tab of item.tabs) {
          considerMatch(item, tab.path);
        }
      }
    }
  }

  return bestMatch;
}

/**
 * Get the active tab ID based on current pathname.
 */
export function getActiveTabId(pathname: string, tabs: Array<{ id: string; path: string }>): string {
  for (const tab of tabs) {
    if (pathname === tab.path || pathname.startsWith(`${tab.path}/`)) {
      return tab.id;
    }
  }
  return tabs[0]?.id || '';
}

/**
 * Find a section by ID.
 */
function findSection(sections: NavSection[], sectionId: string): NavSection | null {
  return sections.find((s) => s.id === sectionId) ?? null;
}

// ============================================================================
// Component
// ============================================================================

const PortalLayoutImpl = (props: PortalLayoutProps): React.ReactElement => {
  // NavigationProgressProvider lives here so every portal gets the GitHub-style
  // top loading bar for free. `PortalLayoutBody` consumes the hook and wraps
  // `onNavigate` so clicks on any sidebar item (main rail or secondary panel)
  // trigger the bar before the router push.
  return (
    <NavigationProgressProvider pathKey={props.pathname}>
      <PortalLayoutBody {...props} />
    </NavigationProgressProvider>
  );
};

const PortalLayoutBody = ({
  children,
  pathname,
  onNavigate: rawOnNavigate,
  onHoverPath,
  navConfig,
  visibilityContext,
  badgeCounts,
  impersonating = false,
  bannerHeight = 0,
  renderHeader,
  renderBreadcrumbs,
  renderBanner,
  onLogoClick,
  user,
  userAvatarPlacement = 'header',
  onLogout,
  onProfile,
  onControlCenter,
  settingsMenuLabel,
  isDarkMode,
  onThemeToggle,
  hoverExpand = false,
}: PortalLayoutProps): React.ReactElement => {
  const theme = useTheme();
  const { startProgress } = useNavigationProgress();
  const { t } = useI18n();

  // Wrap the portal's navigate callback so the progress bar starts the instant
  // a nav item is clicked — covers main rail items, secondary panel items, and
  // classic UnifiedSidebar items (all funnel through onNavigate).
  const onNavigate = useCallback(
    (path: string) => {
      if (path !== pathname) {
        startProgress();
      }
      rawOnNavigate(path);
    },
    [rawOnNavigate, pathname, startProgress],
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const layoutMode = navConfig.layoutMode ?? 'classic';
  const useRail = layoutMode === 'rail' && !isMobile;

  // Rail state (only active in rail mode)
  const rail = useSidebarRail({
    pathname,
    sections: navConfig.sections,
    storageKey: navConfig.storageKey ? `${navConfig.storageKey}-rail` : undefined,
    hoverExpand: useRail && hoverExpand,
  });

  // Memoize visibility-filtered sections to avoid recomputing on every click
  const visibleSections = useMemo(
    () => filterNavSections(navConfig.sections, visibilityContext),
    [navConfig.sections, visibilityContext],
  );

  // When a rail section is clicked, navigate to the alphabetically-first
  // visible item so the main content updates immediately (not just the panel).
  // The sort here mirrors SidebarPanel's render order so the panel's
  // top-of-list item matches the page the user lands on.
  //
  // Three cases:
  //   1. Section has `defaultItemId` (e.g. Home → dashboard) — navigate to that
  //      item's path even if it's not alphabetically first.
  //   2. Section is a single item with tabs (e.g. Relations → tabs flattened
  //      into the panel) — sort the tabs and navigate to the first.
  //   3. Section has multiple items — sort them and navigate to the first.
  //
  // Items are already visibility-filtered via visibleSections (filterNavSections),
  // so section.items only contains items the current user can see.
  const handleSectionClick = useCallback(
    (sectionId: string) => {
      rail.openSection(sectionId);
      const section = visibleSections.find((s) => s.id === sectionId);
      if (!section?.items?.length) return;

      // Skip the navigate call when the user is already on the target path.
      // The rail-click should always "reset to first item" relative to the
      // section, but if they're already there, re-pushing the same path is a
      // pointless re-render (and starts the nav progress bar).
      const goTo = (path: string) => {
        if (path && path !== pathname) onNavigate(path);
      };

      if (section.defaultItemId) {
        const pinned = section.items.find((item) => item.id === section.defaultItemId);
        if (pinned?.path) {
          goTo(pinned.path);
          return;
        }
      }

      // Tab-promotion case: single item with multiple tabs → panel shows the
      // tabs as items. Mirror that here so the rail click also targets the
      // first sorted TAB instead of the (only) parent item.
      if (section.items.length === 1 && section.items[0].tabs && section.items[0].tabs.length > 1) {
        const visibleTabs = section.items[0].tabs.filter(
          (tab) => !tab.visibility || checkVisibility(tab.visibility, visibilityContext),
        );
        const sortedTabs = sortItemsByTranslatedLabel(visibleTabs, t);
        const firstTab = sortedTabs[0];
        if (firstTab?.path) {
          goTo(firstTab.path);
        }
        return;
      }

      const sortedItems = sortItemsByTranslatedLabel(section.items, t);
      const firstItem = sortedItems[0];
      if (firstItem?.path) {
        goTo(firstItem.path);
      }
    },
    [rail, visibleSections, onNavigate, pathname, t, visibilityContext],
  );

  const effectivePanelOpen = rail.panelOpen;

  // Rail width tracks the rail's pinned/unpinned state directly. When
  // the user pins the rail open via the in-rail pin button, the rail
  // widens to RAIL_EXPANDED_WIDTH and the page content area shifts
  // right (push, not overlay) — the previous hover-overlay UX was
  // dropped because it made layout shift unpredictably with mouse
  // proximity. Pin is explicit, persisted, and reflows the content
  // exactly once per click.
  const railWidth = hoverExpand && rail.railExpanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH;
  // Current sidebar width
  const currentSidebarWidth = useRail
    ? rail.fullCollapsed
      ? 0
      : effectivePanelOpen
        ? railWidth + PANEL_WIDTH
        : railWidth
    : DRAWER_WIDTH;

  // Get active nav item and its tabs
  const activeNavItem = useMemo(() => getActiveNavItem(pathname, navConfig), [pathname, navConfig]);

  const activeTabId = useMemo(() => {
    if (activeNavItem?.tabs && activeNavItem.tabs.length > 0) {
      return getActiveTabId(pathname, activeNavItem.tabs);
    }
    return '';
  }, [pathname, activeNavItem]);

  // Calculate layout offset for SplitPanel positioning
  const layoutOffset = useMemo(() => {
    let offset = LAYOUT_HEIGHTS.TOOLBAR_MINIMAL;
    if (impersonating) {
      offset += bannerHeight;
    }
    offset += 8;
    return offset;
  }, [impersonating, bannerHeight]);

  const toggleMobileDrawer = (): void => {
    setMobileOpen((prev) => !prev);
  };

  const handleTabChange = useCallback(
    (_tabId: string, path: string): void => {
      onNavigate(path);
    },
    [onNavigate],
  );

  // Panel section (for rail mode)
  const panelSection = useMemo(
    () => (rail.activeSection ? findSection(navConfig.sections, rail.activeSection) : null),
    [rail.activeSection, navConfig.sections],
  );

  // Section tabs from nav config.
  //
  // When running in rail mode, SidebarPanel has a "single item with tabs →
  // flatten into panel items" rule (see SidebarPanel.tsx). In that case the
  // tabs are already clickable entries in the left panel, so rendering them
  // again as a top tab bar inside PageContentHeader is redundant noise. We
  // mirror SidebarPanel's exact predicate here and hide the top tab bar.
  const sectionTabs = useMemo(() => {
    const tabs = activeNavItem?.tabs ?? [];
    if (!useRail || !panelSection) return tabs;
    const visibleItems = panelSection.items.filter(
      (item) => !item.visibility || checkVisibility(item.visibility, visibilityContext),
    );
    const flattenedIntoSidebar = visibleItems.length === 1 && !!visibleItems[0].tabs && visibleItems[0].tabs.length > 1;
    return flattenedIntoSidebar ? [] : tabs;
  }, [activeNavItem, useRail, panelSection, visibilityContext]);

  // Handle settings click (for rail mode).
  // If the portal provides an explicit onControlCenter handler, prefer it
  // (e.g., business portal navigates to /control-tower, not /settings).
  const handleSettingsClick = useCallback(() => {
    if (onControlCenter) {
      onControlCenter();
    } else if (navConfig.settingsNav) {
      onNavigate(navConfig.settingsNav.path);
    }
  }, [onControlCenter, navConfig.settingsNav, onNavigate]);

  return (
    <SidebarWidthProvider sidebarWidth={currentSidebarWidth}>
      {/* Mounted here so the bar can read useSidebarWidth() and align with
          the content area's top border (not the full viewport). */}
      <NavigationProgressBar />
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          pt: impersonating ? `${String(bannerHeight)}px` : 0,
        }}
      >
        {/* Banner slot (e.g., impersonation banner) - fixed at top */}
        {renderBanner?.()}

        {/* Sidebar: Rail mode vs Classic mode.
            In hoverExpand mode the rail + panel share a hover region so
            moving the mouse from a rail icon onto a panel item keeps the
            panel open. The leave delay is handled inside `useSidebarRail`. */}
        {useRail ? (
          // `display: flex` (not `display: contents`) so the aside has a real
          // layout box for `onMouseEnter` / `onMouseLeave` to fire on. Chrome
          // & Safari don't deliver mouse events reliably to `display: contents`
          // wrappers — the events fire on the children instead, so the
          // hoverExpand UX (keep panel open while moving cursor from rail
          // icon to panel item) silently flickered closed in the gap between
          // the two. The flex shape matches the previous visual layout
          // because rail + panel were already siblings in the outer flex row.
          <Box
            component="aside"
            onMouseEnter={hoverExpand ? () => rail.setHovered(true) : undefined}
            onMouseLeave={hoverExpand ? () => rail.setHovered(false) : undefined}
            // Focus-parity with mouse-parity: keep the panel open while
            // keyboard focus is anywhere within the rail+panel subtree.
            // `onBlur` here uses React's synthetic-event bubbling — only
            // fires when focus moves to an element OUTSIDE this aside.
            // Without this, a screen-reader user who tabs into the panel
            // would have the panel ripped out from under them when the
            // mouse-leave debounce timer fires.
            onFocus={hoverExpand ? () => rail.setHovered(true) : undefined}
            onBlur={
              hoverExpand
                ? (e: React.FocusEvent<HTMLElement>) => {
                    const next = e.relatedTarget as Node | null;
                    if (next && e.currentTarget.contains(next)) return; // focus staying within
                    rail.setHovered(false);
                  }
                : undefined
            }
            sx={{ display: 'flex', height: '100%' }}
          >
            <SidebarRail
              config={navConfig}
              pathname={pathname}
              onNavigate={onNavigate}
              onHoverPath={onHoverPath}
              onSectionClick={handleSectionClick}
              // Hover-preview wiring: when a rail icon is hovered (or focused
              // via keyboard), the panel previews that section without
              // committing. The outer aside's `onMouseLeave` clears it via
              // `setHovered(false)` → `setPreviewSectionState(null)`.
              onSectionHover={hoverExpand ? rail.setPreviewSection : undefined}
              activeSection={rail.activeSection}
              visibilityContext={visibilityContext}
              onSettingsClick={handleSettingsClick}
              hidden={rail.fullCollapsed}
              // The avatar lives in the header by default per the v3 design
              // (shell.jsx:191-194). Only the legacy `'rail'` placement
              // forwards `user` so the bottom-of-rail Asana avatar renders.
              user={userAvatarPlacement === 'rail' ? user : undefined}
              onLogout={onLogout}
              onProfile={onProfile}
              settingsMenuLabel={settingsMenuLabel}
              isDarkMode={isDarkMode}
              onThemeToggle={onThemeToggle}
              // Pinnable rail — when `hoverExpand` is true the rail
              // shows a pin button in its header. Clicking it pushes
              // page content to the right and reveals icon+label rows.
              // (`hoverExpand` is the prop name kept for back-compat
              // with portals that already opt into the new nav UX —
              // the hover behaviour itself was removed.)
              expandable={hoverExpand}
              expanded={rail.railExpanded}
              onPinToggle={rail.toggleRailExpanded}
              // Paint the rail's own right-edge chrome whenever the
              // secondary panel is collapsed — the panel was providing
              // the divider + rounded top-right corner, and dropping
              // both when the panel slides away made the rail look bare.
              showRightEdge={!effectivePanelOpen}
            />
            <SidebarPanel
              section={panelSection}
              open={effectivePanelOpen}
              pathname={pathname}
              onNavigate={onNavigate}
              visibilityContext={visibilityContext}
              badgeCounts={badgeCounts}
              onCollapse={rail.toggleCollapse}
              hidden={rail.fullCollapsed}
              // Surface the pin/unpin semantics in the panel header in
              // hoverExpand mode. `undefined` keeps the existing chevron.
              pinned={hoverExpand ? rail.pinned : undefined}
            />
          </Box>
        ) : (
          <UnifiedSidebar
            config={navConfig}
            pathname={pathname}
            open={isMobile ? mobileOpen : true}
            onToggle={toggleMobileDrawer}
            onNavigate={onNavigate}
            visibilityContext={visibilityContext}
            badgeCounts={badgeCounts}
          />
        )}

        {/* Header (via render prop) — full-width, z-index above sidebar.
            In hoverExpand portals (the new rail UX), the hamburger
            toggles rail expansion (collapsed icon strip ↔ labelled
            column). The collapsed state intentionally has no pin
            button, so the hamburger is the way back into expansion.
            Legacy rails keep the full-collapse hide-everything behavior. */}
        {renderHeader({
          showMenuButton: isMobile || useRail,
          onMenuClick: isMobile
            ? toggleMobileDrawer
            : useRail
              ? hoverExpand
                ? rail.toggleRailExpanded
                : rail.toggleFullCollapse
              : toggleMobileDrawer,
          portalName: navConfig.portalName,
          onLogoClick,
          // v3: avatar lives in the topbar (right side, after the bell)
          // unless the portal explicitly opts back into the rail placement.
          hideUserAvatar: useRail && userAvatarPlacement === 'rail',
        })}

        {/* Main Content — the <main> landmark is owned by PageTemplate */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            width: { xs: '100%', md: `calc(100% - ${String(currentSidebarWidth)}px)` },
            ml: {
              xs: 0,
              md: useRail ? (rail.fullCollapsed ? 0 : effectivePanelOpen ? `${String(PANEL_WIDTH)}px` : 0) : 0,
            },
            transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ minHeight: HEADER_HEIGHT, flexShrink: 0 }} /> {/* Spacer for fixed header */}
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              // Asana-style: top border separates content from top nav
              ...(useRail && {
                borderTop: `1px solid ${theme.palette.divider}`,
              }),
            }}
          >
            {renderBreadcrumbs && <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1 }}>{renderBreadcrumbs()}</Box>}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                p: 0,
                overflow: 'hidden',
              }}
            >
              <SectionTabsProvider
                tabs={sectionTabs}
                activeTab={activeTabId}
                onTabChange={handleTabChange}
                visibilityContext={visibilityContext}
              >
                <LayoutOffsetProvider offset={layoutOffset}>{children}</LayoutOffsetProvider>
              </SectionTabsProvider>
            </Box>
          </Container>
        </Box>
      </Box>
    </SidebarWidthProvider>
  );
};

/**
 * Memoized: the portal shell. When outer contexts (Auth, Business, Realm, etc.)
 * publish new values, PortalLayout bails out of re-rendering as long as its
 * props' references are stable. The portal wrapper (PortalShellInner) is
 * responsible for stabilising callbacks + the visibility context.
 */
export const PortalLayout = memo(PortalLayoutImpl);
